'use client';

import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const relatedServices = [
  { label: 'Container Transport', href: '/container-transport' },
  { label: 'Less Container Load (LCL)', href: '/lcl-transport' },
  { label: 'Airfreight Transport', href: '/airfreight-transport' },
  { label: 'Inland Transport', href: '/inland-transport' },
  { label: 'Customs Representation', href: '/customs-representation' },
  { label: 'Port Operations', href: '/port-operations' },
  { label: 'Cargo Insurance', href: '/cargo-insurance' },
];

export default function BankGuaranteePage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F4F6F9' }}>
      <Header />
      <main className="flex-1 pt-32 pb-20">
        {/* Hero section */}
        <section className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Illustration */}
            <div className="flex items-center justify-center">
              <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-sm lg:max-w-md" aria-label="Bank guarantee financial security illustration">
                <rect x="60" y="110" width="200" height="120" rx="4" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.04)"/>
                <path d="M50 110 L160 50 L270 110 Z" stroke="#1B6B8A" strokeWidth="4" fill="rgba(27,107,138,0.06)" strokeLinejoin="round"/>
                <rect x="80" y="110" width="16" height="120" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="116" y="110" width="16" height="120" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="152" y="110" width="16" height="120" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="188" y="110" width="16" height="120" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="224" y="110" width="16" height="120" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="140" y="180" width="40" height="50" rx="4" stroke="#1B6B8A" strokeWidth="3" fill="rgba(27,107,138,0.08)"/>
                <circle cx="172" cy="207" r="3" stroke="#1B6B8A" strokeWidth="2" fill="none"/>
                <rect x="50" y="230" width="220" height="10" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <rect x="40" y="240" width="240" height="10" rx="2" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <ellipse cx="268" cy="168" rx="20" ry="6" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.08)"/>
                <rect x="248" y="148" width="40" height="20" rx="0" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.06)"/>
                <ellipse cx="268" cy="148" rx="20" ry="6" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.08)"/>
                <rect x="248" y="132" width="40" height="16" rx="0" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.04)"/>
                <ellipse cx="268" cy="132" rx="20" ry="6" stroke="#1B6B8A" strokeWidth="2.5" fill="rgba(27,107,138,0.08)"/>
                <text x="268" y="153" textAnchor="middle" fill="#1B6B8A" fontSize="10" fontFamily="sans-serif" fontWeight="bold">$</text>
                <path d="M160 68 L163 60 L166 68 L174 68 L168 73 L170 81 L163 76 L156 81 L158 73 L152 68 Z" stroke="#2A8FAB" strokeWidth="1.5" fill="rgba(42,143,171,0.1)"/>
              </svg>
            </div>

            {/* Text */}
            <div>
              <div className="w-10 h-1 mb-5 rounded" style={{ background: '#1B6B8A' }} />
              <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight" style={{ color: '#0F2340', fontFamily: 'var(--font-display, sans-serif)' }}>
                Bank guarantee
              </h1>
              <div className="space-y-5 text-base lg:text-lg leading-relaxed" style={{ color: '#3A5070' }}>
                <p>World Transport Overseas provides <strong style={{ color: '#0F2340' }}>bank guarantee services</strong> that give businesses the financial security instruments they need for international trade and customs operations. Our established banking relationships enable us to arrange guarantees quickly and efficiently, supporting your trade finance requirements.</p>
                <p>Whether you need a customs guarantee for duty deferment, a performance bond for a trade contract, or a letter of credit for an international transaction, our financial specialists will identify the most appropriate instrument and arrange it on your behalf. We handle all the documentation and liaise directly with the relevant banking institutions.</p>
                <p>Our bank guarantee service is particularly valuable for businesses engaged in regular import and export activities, providing the financial assurances required by customs authorities, trading partners, and regulatory bodies. WTO's expertise in trade finance ensures you have the right guarantees in place to keep your business moving without unnecessary delays or complications.</p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link href="/transport-quote" className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white font-semibold text-sm transition-all duration-200 hover:opacity-90 shadow-md" style={{ background: '#1A5FAB' }}>
                  Get a Quote
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key highlights */}
        <section className="py-14" style={{ background: '#0F2340' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Why WTO Bank Guarantees?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: 'Customs Guarantees', desc: 'Financial security instruments accepted by customs authorities for duty deferment and transit' },
                { title: 'Trade Finance', desc: 'Letters of credit, performance bonds, and bid bonds for international trade transactions' },
                { title: 'Expert Advice', desc: 'Specialist guidance on the most appropriate guarantee instrument for your transaction' },
                { title: 'Fast Processing', desc: 'Efficient processing of guarantee applications with established banking relationships' },
              ]?.map((h, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <div className="w-8 h-8 rounded-lg mb-4 flex items-center justify-center" style={{ background: 'rgba(27,107,138,0.2)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#1B6B8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.21.45 4.55 1.23" /></svg>
                  </div>
                  <p className="font-bold text-base mb-1" style={{ color: '#FFFFFF' }}>{h?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{h?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guarantee types */}
        <section className="py-14" style={{ background: '#0A1628' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-10" style={{ color: '#FFFFFF' }}>Guarantee Types</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Customs Guarantee', desc: 'Financial security accepted by customs authorities for duty deferment, transit procedures, and warehousing authorisations.' },
                { title: 'Performance Bond', desc: 'Guarantee to a beneficiary that the principal will fulfil contractual obligations — protecting against non-performance.' },
                { title: 'Bid Bond', desc: 'Security provided with a tender submission guaranteeing that the bidder will honour their offer if selected.' },
                { title: 'Advance Payment Guarantee', desc: 'Protection for buyers who make advance payments, ensuring refund if the seller fails to deliver.' },
                { title: 'Letter of Credit', desc: 'Documentary credit providing payment security for international trade transactions between buyers and sellers.' },
                { title: 'Warranty Bond', desc: 'Post-completion guarantee ensuring the principal will rectify defects during the warranty period.' },
              ]?.map((c, i) => (
                <div key={i} className="rounded-2xl p-6" style={{ background: '#132848', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <p className="font-bold text-base mb-2" style={{ color: '#FFFFFF' }}>{c?.title}</p>
                  <p className="text-sm leading-relaxed" style={{ color: '#B8CDE0' }}>{c?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related services */}
        <section className="py-14" style={{ background: '#0E1F38' }}>
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="w-10 h-1 mb-4 rounded" style={{ background: '#1B6B8A' }} />
            <h2 className="text-2xl lg:text-3xl font-bold mb-8" style={{ color: '#FFFFFF' }}>Other Services</h2>
            <div className="flex flex-wrap gap-3">
              {relatedServices?.map((s) => (
                <Link key={s?.href} href={s?.href} className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 hover:opacity-80" style={{ background: '#132848', color: '#B8CDE0', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {s?.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
